import React, { useEffect, useRef, useState } from 'react';

const TIMEOUT_AUTO_SUBMIT = 1500;

/**
 * @returns a controlled <input type="text" /> element that calls `onSubmit` when
 * losing focus (blur) and on 'Enter' key down.
 */
export default function TextInput({ value, onChange, onSubmit, disabled, placeholder, className }) {
  const [text, setText] = useState('');
  const submitTimerRef = useRef(null);
  const inputRef = useRef(null);

  // when the passed in `value` changes, update the component
  useEffect(() => {
    setText(value ?? '');
  }, [value]);

  // the <input> is a controlled component;
  // activate the `onChange` callback on any text change
  function handleChange(e) {
    // clear any existing timer
    if (submitTimerRef.current) {
      clearTimeout(submitTimerRef.current);
    }
    
    const textValue = e.target.value;
    setText(textValue);
    if (onChange) {
      onChange(textValue);
    }

    // delay automatic triggering of onSubmit; pass text value
    submitTimerRef.current = setTimeout(() => triggerSubmit(textValue), TIMEOUT_AUTO_SUBMIT /*ms*/);
  }

  // activate the `onSubmit` callback when losing focus (onBlur)
  function handleBlur(e) {
    triggerSubmit(text);
  }

  // activate the `onSubmit` callback on keypress 'Enter'
  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      triggerSubmit(text);
    }
  }

  // select all text when focused
  function handleFocus(e) {
    e.target.select();
  }

  // trigger the `onSubmit` callback and clear the timer
  function triggerSubmit(text) {
    if (onSubmit) {
      // before submitting, blur the <input> to remove focus
      inputRef.current.blur();

      // clear any existing timer
      if (submitTimerRef.current) {
        clearTimeout(submitTimerRef.current);
      }
      // activate the `onSubmit` callback
      onSubmit(text);
    }
  }

  return <>
    <input value={text} 
        ref={inputRef}
        disabled={disabled}
        placeholder={placeholder}
        className={className}
        onChange={handleChange} 
        onBlur={handleBlur} 
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
      />
  </>;
}